import React, { useState } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import ListHeader from "./ListHeader";
import ListFilterBar from "./ListFilterBar";
import ListDocumentTable from "./ListDocumentTable";

/**
 * Renders the footer displaying the total document count.
 * @param {object} props
 * @param {number} props.count - Total number of documents
 * @returns {React.ReactElement}
 */
function ListFooter({ count }) {
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box mt="16px" p="16px" borderRadius="14px" bg={boxBg}>
            <Text textTransform="uppercase" fontSize="0.7rem" fontWeight="600" letterSpacing="1px"
                mb="4px" color={labelColor}>
                Total Documents
            </Text>
            <Text fontWeight="800" fontSize="1.8rem" lineHeight="1" color={valueColor}>{count}</Text>
        </Box>
    );
}

/**
 * Displays a filterable list of legal documents with type filter,
 * latest-only toggle, and total count footer.
 * @param {object} props
 * @param {Array} props.documents - List of legal documents
 * @param {object|null} props.selectedDoc - Currently selected document
 * @param {function} props.setSelectedDoc - Setter for selected document
 * @param {boolean} props.loading - Loading state
 * @param {Array} props.types - Available document types from API
 * @param {string} props.filterType - Current type filter value
 * @param {function} props.setFilterType - Setter for type filter
 * @param {string} props.filterLanguage - Current language filter value
 * @param {function} props.setFilterLanguage - Setter for language filter
 * @param {boolean} props.latestOnly - Whether to show only latest versions
 * @param {function} props.setLatestOnly - Setter for latest filter
 * @param {function} props.onRefresh - Callback to refresh document list
 * @param {function} props.onNewDocument - Callback to create a new document
 * @returns {React.ReactElement}
 */
function LegalDocumentList({
    documents, selectedDoc, setSelectedDoc, loading,
    types, filterType, setFilterType, filterLanguage, setFilterLanguage,
    latestOnly, setLatestOnly, onRefresh, onNewDocument
}) {
    const [refreshing, setRefreshing] = useState(false);

    /** Triggers a refresh with loading indicator. */
    const handleRefresh = async () => {
        setRefreshing(true);
        try { await onRefresh(); } finally { setRefreshing(false); }
    };

    return (
        <Card>
            <ListHeader refreshing={refreshing} onRefresh={handleRefresh} onNewDocument={onNewDocument} />
            <ListFilterBar types={types} filterType={filterType} setFilterType={setFilterType}
                filterLanguage={filterLanguage} setFilterLanguage={setFilterLanguage}
                latestOnly={latestOnly} setLatestOnly={setLatestOnly} />
            <ListDocumentTable documents={documents} selectedDoc={selectedDoc}
                setSelectedDoc={setSelectedDoc} loading={loading || refreshing} />
            <ListFooter count={documents.length} />
        </Card>
    );
}

export default LegalDocumentList;
